/**
 * Date and time manipulation utilities including MyDate and MyTime classes,
 * date parsing, formatting, and arithmetic operations.
 */

// Constants
const months = [
  "January",
  "Feburary",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const monthsShort = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "June",
  "July",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];
const daysShort = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const monthStartDay = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
const TIMESTAMP = "TIMESTAMP";
const NUMERIC_MONTH = 2,
  SHORT_MONTH = 3,
  LONG_MONTH = 4;
const NO_YEAR = 0,
  SHORT_YEAR = 2,
  FULL_YEAR = 4;
const dateSeparators = "/-\\. ";
const timeSeparators = ".:";
const NUMERIC = "0123456789";
const monthList = "jan,feb,mar,apr,may,jun,jul,aug,sep,oct,nov,dec";

// Utility functions
function twoDigit(num: number): string {
  if (num == 0) return "00";
  return num < 10 ? "0" + num : String(num);
}

function parseDateTime(str: string, separators: string): string[] {
  str = extract(str, NUMERIC + separators);
  const tokens: string[] = [];
  let curToken = 0;
  for (let i = 0; i < str.length; i++)
    if (separators.indexOf(str.charAt(i)) != -1) {
      if (tokens[curToken] != null) ++curToken;
    } else {
      if (tokens[curToken] == null) tokens[curToken] = "";
      tokens[curToken] += str.charAt(i);
    }
  return tokens;
}

function parseMonthName(str: string): number {
  str = str.toLowerCase();
  for (let i = 0; i < 12; i++)
    if (str.indexOf(monthList.substring(i * 4, i * 4 + 3)) != -1) return i;
  return 12;
}

function smartDate(str: string, tokens: string[]): string[] {
  const out: string[] = [];
  let i = parseMonthName(str);

  if (i < 12) {
    let inNum: boolean,
      num = "",
      monthBegin = -1;
    out[0] = String(i + 1);

    inNum = false;
    for (i = 0; i < str.length; i++)
      if (NUMERIC.indexOf(str.charAt(i)) != -1) {
        num += str.charAt(i);
        inNum = true;
      } else if (inNum == true) break;
      else monthBegin = i;
    if (num == "") {
      out[1] = "1";
      out[2] = String(currentYear());
      return out;
    }
    out[1] = num;

    inNum = false;
    num = "";
    for (i = str.length - 1; i >= 0; i--)
      if (NUMERIC.indexOf(str.charAt(i)) != -1) {
        num = str.charAt(i) + num;
        inNum = true;
      } else if (inNum == true) break;

    if (i != monthBegin) {
      out[2] = num;
    } else {
      if (out[1].charAt(0) == "0" || parseInt(out[1], 10) > 31) {
        out[1] = "1";
        out[2] = num;
      } else {
        out[2] = String(currentYear());
      }
    }
    if (out[1].length == 4) {
      const temp = out[1];
      out[1] = out[2];
      out[2] = temp;
    }
  } else {
    if (tokens.length == 2 || (tokens.length == 3 && tokens[2] == "")) {
      tokens[2] = String(currentYear());
      return tokens;
    }
    str = extract(str, NUMERIC);
    if (str.length < 4 || str.length > 8) return out;
    if (str.length == 4) {
      // mdyy
      out[0] = str.charAt(0);
      out[1] = str.charAt(1);
      out[2] = str.substring(2);
    } else if (str.length == 5 || str.length == 7) {
      // mddyy(yy) or mmdyy(yy)
      if (
        str.charAt(0) == "0" ||
        (str.charAt(0) == "1" && str.charAt(1) <= "2" && str.charAt(2) != "0")
      ) {
        out[0] = str.substring(0, 2);
        out[1] = str.charAt(2);
      } else {
        out[0] = str.charAt(0);
        out[1] = str.substring(1, 3);
      }
      out[2] = str.substring(3);
    } else if (str.length == 6) {
      // mdyyyy or mmddyy
      if (
        str.substring(0, 3).indexOf("0") == -1 &&
        (str.substring(0, 2) > "12" ||
          str.substring(2, 4) > "31" ||
          str.substring(2, 4) == "19" ||
          str.substring(2, 4) == "20")
      ) {
        out[0] = str.charAt(0);
        out[1] = str.charAt(1);
        out[2] = str.substring(2);
      } else {
        out[0] = str.substring(0, 2);
        out[1] = str.substring(2, 4);
        out[2] = str.substring(4);
      }
    } else if (str.length == 8) {
      // mmddyyyy
      out[0] = str.substring(0, 2);
      out[1] = str.substring(2, 4);
      out[2] = str.substring(4);
    }
  }
  return out;
}

function parseDate(dateVal: string): string[] {
  const tokens = parseDateTime(dateVal, dateSeparators);
  if (
    tokens.length != 3 ||
    tokens[0] == "" ||
    tokens[1] == "" ||
    tokens[2] == ""
  )
    return smartDate(dateVal, tokens);
  if (tokens[0].length == 4) {
    const temp = tokens[0];
    tokens[0] = tokens[1];
    tokens[1] = tokens[2];
    tokens[2] = temp;
  }
  return tokens;
}

function parseTime(timeVal: string): string[] {
  const tokens = parseDateTime(timeVal, timeSeparators);
  if (tokens.length > 1 || (tokens[0] && tokens[0] > "")) {
    if (tokens.length == 1) {
      if (tokens[0].length < 3) {
        tokens[1] = "00";
      } else {
        tokens[1] = tokens[0].substring(tokens[0].length - 2);
        tokens[0] = tokens[0].substring(0, tokens[0].length - 2);
      }
    }
    if (tokens.length == 2) tokens[2] = "00";
    const hour = parseInt(tokens[0], 10);
    if (hour >= 1 && hour < 12 && timeVal.toLowerCase().indexOf("p") != -1)
      tokens[0] = String(hour + 12);
    else if (hour == 12 && timeVal.toLowerCase().indexOf("a") != -1)
      tokens[0] = String(hour - 12);
  } else {
    tokens.length = 0;
  }
  return tokens;
}

function extract(str: string, mask: string): string {
  let out = "";
  for (let i = 0; i < str.length; i++)
    if (mask.indexOf(str.charAt(i)) != -1) out += str.charAt(i);
  return out;
}

function isLeapYear(year: number): boolean {
  return year % 4 == 0 && (year % 100 != 0 || year % 400 == 0);
}

function daysInYear(year: number): number {
  return isLeapYear(year) ? 366 : 365;
}

function currentYear(): number {
  let year = currentDateTime().getFullYear();
  if (year < 1000) year += 1900;
  return year;
}

// MyDate class
class MyDate {
  year: number | null = 0;
  month: number = 0;
  day: number = 0;

  setFromDate(date: Date): this {
    this.year = date.getFullYear();
    this.month = date.getMonth() + 1;
    this.day = date.getDate();
    return this;
  }

  setFromString(dateStr: string, partition?: number): this {
    if (dateStr == TIMESTAMP) return this.setFromDate(currentDateTime());
    const tokens = parseDate(dateStr);
    if (
      tokens.length != 3 ||
      tokens[0] == "" ||
      tokens[1] == "" ||
      tokens[2] == ""
    ) {
      this.year = null;
      return this;
    }
    this.month = parseInt(tokens[0], 10);
    this.day = parseInt(tokens[1], 10);
    this.year = parseInt(tokens[2], 10);
    if (this.year < 100) {
      if (partition == null) partition = (currentYear() + 20) % 100;
      this.year = 2000 + (this.year >= partition ? this.year - 100 : this.year);
    }
    return this;
  }

  setFromInt(dateInt: number): this {
    const newDate = new Date();
    newDate.setTime(dateInt);
    this.setFromDate(newDate);
    return this;
  }

  setFromValues(newYear: number, newMonth: number, newDay: number): this {
    this.year = newYear;
    this.month = newMonth;
    this.day = newDay;
    return this;
  }

  getAsString(monthFormat?: number, yearFormat?: number): string {
    let out = "",
      yearSpacer = ", ";
    if (monthFormat == SHORT_MONTH) {
      out += this.getMonthName(true) + " " + this.day;
    } else if (monthFormat != null && monthFormat >= LONG_MONTH) {
      out += this.getMonthName(false) + " " + this.day;
    } else {
      out += twoDigit(this.month) + "/" + twoDigit(this.day);
      yearSpacer = "/";
    }
    if (yearFormat == SHORT_YEAR) {
      out += yearSpacer + twoDigit((this.year ?? 0) % 100);
    } else if (yearFormat != NO_YEAR) {
      out += yearSpacer + this.year;
    }
    return out;
  }

  getAsDate(date?: Date): Date {
    if (date != null) {
      date.setDate(this.day);
      date.setMonth(this.month - 1);
      date.setFullYear(this.year ?? 0);
      return date;
    }
    return new Date(this.year ?? 0, this.month - 1, this.day);
  }

  getAsInt(): number {
    return this.getAsDate().getTime();
  }

  compare(myDate2: MyDate): number {
    return this.getAsInt() - myDate2.getAsInt();
  }

  diff(myDate2: MyDate): number {
    if (myDate2.compare(this) > 0) return -myDate2.diff(this);
    if (this.year == myDate2.year)
      return this.getDayOfYear() - myDate2.getDayOfYear();
    let result =
      daysInYear(myDate2.year ?? 0) -
      myDate2.getDayOfYear() +
      this.getDayOfYear();
    let year = (myDate2.year ?? 0) + 1;
    while ((this.year ?? 0) > year) {
      result += daysInYear(year++);
    }
    return result;
  }

  checkIsValid(): string {
    if (this.year == null) return "Please enter a valid date.";
    if (this.year < 100) return "The date requires a 4 digit year.";
    if (this.year < 1000) return "The year must be after 1000 AD.";
    if (this.month < 1 || this.month > 12)
      return this.month + " is not a valid month.";
    if (this.day > this.getDaysInMonth())
      return this.month == 2 && this.day == 29
        ? this.year + " is not a leap year."
        : this.getMonthName() + " does not have " + this.day + " days.";
    if (this.day < 1) return this.day + " is not a valid day of month.";
    return "";
  }

  makeValid(noMonthRollover?: boolean): this {
    while (this.month < 1 || this.day < 1) {
      if (this.month < 1) {
        this.year = (this.year ?? 0) - 1;
        this.month += 12;
      } else {
        this.month--;
        this.day += this.month < 1 ? 31 : this.getDaysInMonth();
      }
    }
    while (this.month > 12 || this.day > this.getDaysInMonth()) {
      if (this.month > 12) {
        this.month -= 12;
        this.year = (this.year ?? 0) + 1;
      } else if (noMonthRollover) {
        this.day = this.getDaysInMonth();
      } else {
        this.day -= this.getDaysInMonth();
        this.month++;
      }
    }
    return this;
  }

  addDays(num: number): this {
    this.day += num;
    this.makeValid();
    return this;
  }

  addWeeks(num: number): this {
    this.addDays(num * 7);
    return this;
  }

  addMonths(num: number, noRollover?: boolean): this {
    this.month += num;
    this.makeValid(noRollover);
    return this;
  }

  addYears(num: number, noMonthRollover?: boolean): this {
    this.year = (this.year ?? 0) + num;
    this.makeValid(noMonthRollover);
    return this;
  }

  getDayOfYear(): number {
    let dayOfYear = monthStartDay[this.month - 1] + this.day;
    if (this.month > 2 && isLeapYear(this.year ?? 0)) dayOfYear++;
    return dayOfYear;
  }

  getDaysInMonth(): number {
    if (
      this.month == 4 ||
      this.month == 6 ||
      this.month == 9 ||
      this.month == 11
    )
      return 30;
    if (this.month != 2) return 31;
    if (isLeapYear(this.year ?? 0)) return 29;
    return 28;
  }

  getDayOfWeek(): number {
    return this.getAsDate().getDay();
  }

  getDayOfWeekName(Short?: boolean): string {
    if (Short == true) return daysShort[this.getDayOfWeek()];
    return daysOfWeek[this.getDayOfWeek()];
  }

  getWeekInMonth(): number {
    return Math.floor(
      (this.day -
        (((this.day + 6 - this.getDayOfWeek() - 1) % 7) + 1) +
        6) /
        7
    );
  }

  setWeekInMonth(weekNum: number): this {
    this.addWeeks(weekNum - this.getWeekInMonth());
    return this;
  }

  getDayOfWeekInMonth(): number {
    return Math.floor((this.day - 1) / 7) + 1;
  }

  getMaxDayOfWeekInMonth(): number {
    return this.getDaysInMonth() - ((this.day - 1) % 7) > 28 ? 5 : 4;
  }

  getMonthName(Short?: boolean): string {
    if (Short == true) return monthsShort[this.month - 1];
    return months[this.month - 1];
  }
}

// MyTime class
class MyTime {
  hour: number | null = 0;
  minute: number = 0;
  second: number = 0;

  setFromString(timeStr: string): this {
    if (timeStr == TIMESTAMP) return this.setFromDate(currentDateTime());
    const tokens = parseTime(timeStr);
    if (tokens.length < 3 || tokens[0] == "") {
      this.hour = null;
      return this;
    }
    for (let i = 0; i < tokens.length; i++) {
      const val = parseInt(tokens[i], 10);
      tokens[i] = isNaN(val) ? "0" : String(val);
    }
    this.hour = parseInt(tokens[0], 10);
    this.minute = parseInt(tokens[1], 10);
    this.second = parseInt(tokens[2], 10);
    return this;
  }

  setFromDate(date: Date): this {
    this.hour = date.getHours();
    this.minute = date.getMinutes();
    this.second = date.getSeconds();
    return this;
  }

  setFromInt(timeInt: number): this {
    while (timeInt < 0) timeInt += 24 * 60 * 60;
    this.hour = Math.floor(timeInt / 3600) % 24;
    this.minute = Math.floor(timeInt / 60) % 60;
    this.second = timeInt % 60;
    return this;
  }

  setFromValues(newHour: number, newMinute: number, newSecond: number): this {
    this.hour = newHour;
    this.minute = newMinute;
    this.second = newSecond;
    return this;
  }

  getAsString(showSeconds?: boolean, military?: boolean): string {
    let timeOut = "";
    if (military) {
      timeOut = twoDigit(this.hour ?? 0) + ":" + twoDigit(this.minute);
      if (showSeconds) timeOut += ":" + twoDigit(this.second);
    } else {
      let meridian = "am";
      let hour = this.hour ?? 0;
      if (hour > 12) {
        hour -= 12;
        meridian = "pm";
      } else if (hour == 12) {
        meridian = "pm";
      } else if (hour == 0) hour += 12;
      timeOut = hour + ":" + twoDigit(this.minute);
      if (showSeconds) timeOut += ":" + twoDigit(this.second);
      timeOut += meridian;
    }
    return timeOut;
  }

  getAsDate(date?: Date): Date {
    if (date != null) {
      date.setHours(this.hour ?? 0);
      date.setMinutes(this.minute);
      date.setSeconds(this.second);
      return date;
    }
    return new Date(1980, 0, 1, this.hour ?? 0, this.minute, this.second);
  }

  getAsInt(): number {
    return (this.hour ?? 0) * 3600 + this.minute * 60 + this.second;
  }

  addSeconds(num: number): this {
    this.second += num;
    this.makeValid();
    return this;
  }

  addMinutes(num: number): this {
    this.minute += num;
    this.makeValid();
    return this;
  }

  addHours(num: number): this {
    this.hour = (this.hour ?? 0) + num;
    this.makeValid();
    return this;
  }

  compare(myTime2: MyTime): number {
    return this.getAsInt() - myTime2.getAsInt();
  }

  diff(myTime2: MyTime): number {
    return this.getAsInt() - myTime2.getAsInt();
  }

  checkIsValid(): string {
    if (this.hour == null) return "Please enter a valid time.";
    if (this.hour >= 24) return "There are only 24 hours in a day";
    if (this.minute >= 60) return "There are only 60 minutes in an hour";
    if (this.second >= 60) return "There are only 60 seconds in a minute";
    return "";
  }

  makeValid(): this {
    this.setFromInt(this.getAsInt());
    return this;
  }
}

if (typeof _SR_ !== "undefined" && _SR_ != null) {
  _SR_.notify("datetools.js");
}

export {
  MyDate,
  MyTime,
  months,
  monthsShort,
  daysOfWeek,
  daysShort,
  monthStartDay,
  TIMESTAMP,
  NUMERIC_MONTH,
  SHORT_MONTH,
  LONG_MONTH,
  NO_YEAR,
  SHORT_YEAR,
  FULL_YEAR,
  dateSeparators,
  timeSeparators,
  NUMERIC,
  monthList,
  twoDigit,
  parseDateTime,
  parseDate,
  parseTime,
  parseMonthName,
  smartDate,
  extract,
  isLeapYear,
  daysInYear,
  currentYear,
};

// Expose globally for backwards compatibility
const _global = globalThis as Record<string, unknown>;
_global.MyDate = MyDate;
_global.MyTime = MyTime;
_global.months = months;
_global.monthsShort = monthsShort;
_global.daysOfWeek = daysOfWeek;
_global.daysShort = daysShort;
_global.monthStartDay = monthStartDay;
_global.TIMESTAMP = TIMESTAMP;
_global.NUMERIC_MONTH = NUMERIC_MONTH;
_global.SHORT_MONTH = SHORT_MONTH;
_global.LONG_MONTH = LONG_MONTH;
_global.NO_YEAR = NO_YEAR;
_global.SHORT_YEAR = SHORT_YEAR;
_global.FULL_YEAR = FULL_YEAR;
_global.dateSeparators = dateSeparators;
_global.timeSeparators = timeSeparators;
_global.NUMERIC = NUMERIC;
_global.monthList = monthList;
_global.twoDigit = twoDigit;
_global.parseDateTime = parseDateTime;
_global.parseDate = parseDate;
_global.parseTime = parseTime;
_global.parseMonthName = parseMonthName;
_global.smartDate = smartDate;
_global.extract = extract;
_global.isLeapYear = isLeapYear;
_global.daysInYear = daysInYear;
_global.currentYear = currentYear;

// MD (MyDate) function wrappers for backwards compatibility with validatedate.ts and validatedatetime.ts
// These were originally standalone functions that are now class methods
function setFromIntMD(this: MyDate, dateInt: number): MyDate {
  // Must implement directly - this function gets assigned AS setFromInt on MonthYear objects
  const newDate = new Date();
  newDate.setTime(dateInt);
  this.setFromDate(newDate);
  return this;
}
function setFromDateMD(this: MyDate, date: Date): MyDate {
  // Must implement directly - this function gets assigned AS setFromDate on MonthYear objects
  this.year = date.getFullYear();
  this.month = date.getMonth() + 1;
  this.day = date.getDate();
  return this;
}
function setFromStringMD(this: MyDate, dateStr: string, partition?: number): MyDate {
  return this.setFromString(dateStr, partition);
}
function setFromValuesMD(this: MyDate, newYear: number, newMonth: number, newDay: number): MyDate {
  return this.setFromValues(newYear, newMonth, newDay);
}
function getAsStringMD(this: MyDate, monthFormat?: number, yearFormat?: number): string {
  return this.getAsString(monthFormat, yearFormat);
}
function getAsDateMD(this: MyDate, date?: Date): Date {
  // Must implement directly - this function gets assigned AS getAsDate on MonthYear objects
  if (date != null) {
    date.setDate(this.day);
    date.setMonth(this.month - 1);
    // setFullYear instead of deprecated setYear - safe because this.year is always
    // converted to 4-digit by setFromString/setFromStringMY before getAsDate is called
    date.setFullYear(this.year ?? 0);
    return date;
  }
  return new Date(this.year ?? 0, this.month - 1, this.day);
}
function getAsIntMD(this: MyDate): number {
  // Must use getAsDate().getTime() directly, not this.getAsInt()
  // because this function gets assigned AS getAsInt on MonthYear objects
  return this.getAsDate().getTime();
}
function compareMD(this: MyDate, myDate2: MyDate): number {
  return this.compare(myDate2);
}
function diffMD(this: MyDate, myDate2: MyDate): number {
  return this.diff(myDate2);
}
function checkIsValidMD(this: MyDate): string {
  // Must implement directly - this function gets assigned AS checkIsValid on MonthYear objects
  if (this.year == null) return "Please enter a valid date.";
  if (this.year < 100) return "The date requires a 4 digit year.";
  if (this.year < 1000) return "The year must be after 1000 AD.";
  if (this.month < 1 || this.month > 12) return this.month + " is not a valid month.";
  if (this.day > this.getDaysInMonth())
    return (this.month == 2 && this.day == 29) ? (this.year + " is not a leap year.") : (this.getMonthName() + " does not have " + this.day + " days.");
  if (this.day < 1)
    return this.day + " is not a valid day of month.";
  return "";
}
function makeValidMD(this: MyDate, noMonthRollover?: boolean): MyDate {
  return this.makeValid(noMonthRollover);
}
function addDaysMD(this: MyDate, num: number): MyDate {
  return this.addDays(num);
}
function addWeeksMD(this: MyDate, num: number): MyDate {
  return this.addWeeks(num);
}
function addMonthsMD(this: MyDate, num: number, noRollover?: boolean): MyDate {
  return this.addMonths(num, noRollover);
}
function addYearsMD(this: MyDate, num: number, noMonthRollover?: boolean): MyDate {
  return this.addYears(num, noMonthRollover);
}
function getDayOfYearMD(this: MyDate): number {
  return this.getDayOfYear();
}
function getDaysInMonthMD(this: MyDate): number {
  // Must implement directly - this function gets assigned AS getDaysInMonth on MonthYear objects
  if (this.month == 4 || this.month == 6 || this.month == 9 || this.month == 11)
    return 30;
  if (this.month != 2)
    return 31;
  if (isLeapYear(this.year ?? 0))
    return 29;
  return 28;
}
function getDayOfWeekMD(this: MyDate): number {
  return this.getDayOfWeek();
}
function getDayOfWeekNameMD(this: MyDate, short?: boolean): string {
  return this.getDayOfWeekName(short);
}
function getWeekInMonthMD(this: MyDate): number {
  return this.getWeekInMonth();
}
function setWeekInMonthMD(this: MyDate, weekNum: number): MyDate {
  return this.setWeekInMonth(weekNum);
}
function getDayOfWeekInMonthMD(this: MyDate): number {
  return this.getDayOfWeekInMonth();
}
function getMaxDayOfWeekInMonthMD(this: MyDate): number {
  return this.getMaxDayOfWeekInMonth();
}
function getMonthNameMD(this: MyDate, short?: boolean): string {
  // Must implement directly - this function gets assigned AS getMonthName on MonthYear objects
  if (short == true)
    return monthsShort[this.month - 1];
  return months[this.month - 1];
}

// MT (MyTime) function wrappers for backwards compatibility
function setFromStringMT(this: MyTime, timeStr: string): MyTime {
  return this.setFromString(timeStr);
}
function setFromDateMT(this: MyTime, date: Date): MyTime {
  return this.setFromDate(date);
}
function setFromIntMT(this: MyTime, timeInt: number): MyTime {
  return this.setFromInt(timeInt);
}
function setFromValuesMT(this: MyTime, newHour: number, newMinute: number, newSecond: number): MyTime {
  return this.setFromValues(newHour, newMinute, newSecond);
}
function getAsStringMT(this: MyTime, showSeconds?: boolean, military?: boolean): string {
  return this.getAsString(showSeconds, military);
}
function getAsDateMT(this: MyTime, date?: Date): Date {
  return this.getAsDate(date);
}
function getAsIntMT(this: MyTime): number {
  // Must calculate directly, not call this.getAsInt()
  // because this function gets assigned AS getAsInt on MyTime objects
  return (this.hour ?? 0) * 3600 + this.minute * 60 + this.second;
}
function addSecondsMT(this: MyTime, num: number): MyTime {
  return this.addSeconds(num);
}
function addMinutesMT(this: MyTime, num: number): MyTime {
  return this.addMinutes(num);
}
function addHoursMT(this: MyTime, num: number): MyTime {
  return this.addHours(num);
}
function compareMT(this: MyTime, myTime2: MyTime): number {
  return this.compare(myTime2);
}
function diffMT(this: MyTime, myTime2: MyTime): number {
  return this.diff(myTime2);
}
function checkIsValidMT(this: MyTime): string {
  return this.checkIsValid();
}
function makeValidMT(this: MyTime): MyTime {
  return this.makeValid();
}

// Export MD/MT functions to globalThis for validatedate.ts and validatedatetime.ts
_global.setFromIntMD = setFromIntMD;
_global.setFromDateMD = setFromDateMD;
_global.setFromStringMD = setFromStringMD;
_global.setFromValuesMD = setFromValuesMD;
_global.getAsStringMD = getAsStringMD;
_global.getAsDateMD = getAsDateMD;
_global.getAsIntMD = getAsIntMD;
_global.compareMD = compareMD;
_global.diffMD = diffMD;
_global.checkIsValidMD = checkIsValidMD;
_global.makeValidMD = makeValidMD;
_global.addDaysMD = addDaysMD;
_global.addWeeksMD = addWeeksMD;
_global.addMonthsMD = addMonthsMD;
_global.addYearsMD = addYearsMD;
_global.getDayOfYearMD = getDayOfYearMD;
_global.getDaysInMonthMD = getDaysInMonthMD;
_global.getDayOfWeekMD = getDayOfWeekMD;
_global.getDayOfWeekNameMD = getDayOfWeekNameMD;
_global.getWeekInMonthMD = getWeekInMonthMD;
_global.setWeekInMonthMD = setWeekInMonthMD;
_global.getDayOfWeekInMonthMD = getDayOfWeekInMonthMD;
_global.getMaxDayOfWeekInMonthMD = getMaxDayOfWeekInMonthMD;
_global.getMonthNameMD = getMonthNameMD;
_global.setFromStringMT = setFromStringMT;
_global.setFromDateMT = setFromDateMT;
_global.setFromIntMT = setFromIntMT;
_global.setFromValuesMT = setFromValuesMT;
_global.getAsStringMT = getAsStringMT;
_global.getAsDateMT = getAsDateMT;
_global.getAsIntMT = getAsIntMT;
_global.addSecondsMT = addSecondsMT;
_global.addMinutesMT = addMinutesMT;
_global.addHoursMT = addHoursMT;
_global.compareMT = compareMT;
_global.diffMT = diffMT;
_global.checkIsValidMT = checkIsValidMT;
_global.makeValidMT = makeValidMT;
